import { Component, ReactElement } from 'react';
import { HelmetWithTitleAndDescription } from '../../../../../../shared/frontend/components/helmetHelper';
import { DisableAdsOnPage } from '../../../../../../shared/frontend/components/ads/disableAdsOnPage';
import { Link, TrackedLink } from '../../../../../../shared/frontend/components/link';
import styles from './policyPageStyles';
import { namedRoutes } from '../../bundles/routes';

/* tslint:disable:max-line-length */
/* tslint:disable:comment-format */

export default class PrivacyPolicyPage extends Component<any, {}> {
  public componentDidMount() {
    if (window.location.hash) {
      (window as any).rioScrollToAnchor(window.location.hash.substr(1));
    }
  }

  public render(): ReactElement<any> {
    return (
      <>
        <HelmetWithTitleAndDescription title="Privacy Policy" />
        <DisableAdsOnPage />
        <div className="slds-p-around--large text-white" style={{ backgroundColor: '#4f4b49' }}>
          <div className={styles.legals}>
            <h1 className="slds-text-heading--large slds-text-body--normal slds-m-bottom--large">Privacy Policy</h1>
            <p>Revised: Feb 22nd, 2026</p>

            <p>
              Thank you for choosing to be part of our community at RaiderIO, Inc. dba Raider.IO (“Raider.IO”, “we”,
              “us”, or “our”).
            </p>
            <p>
              In this privacy policy, we seek to explain to you what information we collect, how we use it, and what
              rights you have in relation to it. This privacy policy applies to all information collected through our
              websites (such as https://raider.io), the Raider.IO Addon, the Raider.IO Desktop Client, the Raider.IO
              Developer API, and/or any related services, sales, communications, marketing, or events (we refer to them
              collectively in this privacy policy as the "Sites").
            </p>
            <p>
              We hope you take some time to read through the privacy policy carefully, as it is important. By using the
              Sites, you agree to our <Link to="/terms-of-use">Terms of Use</Link> and consent to our collection, use
              and disclosure practices, and other activities as described in this privacy policy. If there are any terms
              in this privacy policy that you do not agree with, please discontinue use of our Sites.
            </p>
            <p>
              If you have any questions or concerns about our privacy policy, or our practices with regards to your
              information, please contact us at privacy@raider.io.
            </p>
            <h2>TABLE OF CONTENTS</h2>
            <p>1. WHAT INFORMATION DO WE COLLECT?</p>
            <p>2. HOW DO WE USE YOUR INFORMATION?</p>
            <p>3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</p>
            <p>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</p>
            <p>5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</p>
            <p>6. WHAT IS OUR STANCE ON ANALYTICS AND ADVERTISING?</p>
            <p>7. HOW LONG DO WE KEEP YOUR INFORMATION?</p>
            <p>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</p>
            <p>9. DO WE COLLECT INFORMATION FROM CHILDREN?</p>
            <p>10. WHAT ARE YOUR PRIVACY RIGHTS?</p>
            <p>11. DO WE MAKE UPDATES TO THIS POLICY?</p>
            <p>12. HOW CAN YOU CONTACT US ABOUT THIS POLICY?</p>
            <p>13. ADDITIONAL DISCLOSURES FOR NEVADA RESIDENTS</p>
            <p>14. ADDITIONAL DISCLOSURES FOR CALIFORNIA RESIDENTS</p>
            <p>15. ADDITIONAL DISCLOSURES FOR DATA SUBJECTS IN EUROPE</p>

            <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
            <h3>Information you disclose to us</h3>
            <p>
              <i>
                In Short: We collect information that you provide to us such as username, contact information, and
                account credentials.
              </i>
            </p>
            <p>
              We collect information that you voluntarily provide to us when registering for the Sites, expressing an
              interest in obtaining information about us or our products and services, accessing our content or
              participating in activities on the Sites (such as customizing your profile or entering competitions,
              contests, or giveaways), or otherwise contacting us.
            </p>
            <p>
              The information that we collect depends on the context of your interactions with us and the Sites, the
              choices you make, and the products and features you use. The categories of information we collect, and
              have collected in the past 12 months, include the following:
            </p>
            <ul className={styles.ul}>
              <li>
                Contact Data. We collect your name, email address, postal address, and other similar contact data.
              </li>

              <li>
                Account Credentials. We collect your username, password, and similar security information used for
                authentication and account access.
              </li>

              <li>Demographic Data. We collect your preferred language and similar information. </li>

              <li>
                Profile Data. We collect information relating to your interests, inferences, characters, and gameplay.
              </li>

              <li>
                Content. We collect content within any messages you send to us (such as feedback and questions to
                customer support) or publicly post on the Sites. We also collect any content within any messages you
                exchange with other users through the Sites to facilitate those conversations (such as if you use our
                chat functionality).
              </li>
            </ul>

            <p>
              You may choose to voluntarily provide other information to us that we do not request, and, in such
              instances, you are solely responsible for such information.
            </p>
            <h3>Information automatically collected</h3>
            <p>
              <i>
                In Short: Some information – such as IP address and/or browser and device characteristics – is collected
                automatically when you visit our Sites.
              </i>
            </p>
            <p>
              We automatically collect certain information when you visit our websites or view our emails or ads. We
              also automatically collect information when you use the Raider.IO Addon and/or the Raider.IO Desktop
              Client.
            </p>
            <p>
              The categories of information we automatically collect, and have collected in the past 12 months, include:
            </p>
            <ul className={styles.ul}>
              <li>
                Service Use Data, including data about features you use, pages you visit, emails and advertisements you
                view, products and services you view and purchase, the time of day you browse, and your referring and
                exiting pages.
              </li>

              <li>
                Device Data, including, your IP address, browser and device characteristics, operating system, language
                preferences, and device name.
              </li>

              <li>
                Location Data, including imprecise location data (such as location derived from an IP address or data
                that indicates a city or postal code level).
              </li>

              <li>Character and Combat Log Data through the Raider.IO Addon and the Raider.IO Desktop Client.</li>
            </ul>

            <p>
              For information on how we automatically collect this information, please see the “Do We Use Cookies And
              Other Tracking Technologies?” section below.
            </p>
            <h3>Information collected from Blizzard</h3>
            <p>
              <i>In Short: We collect information from video game publisher Blizzard.</i>
            </p>
            <p>
              We obtain information about you from video game publisher Blizzard Entertainment (“Blizzard”) as part of
              being an authenticated developer with Blizzard, including information relating to your characters,
              gameplay, and achievements. We obtain the information through the Blizzard Developer API made available to
              us and other Blizzard sources, and process the information as a service provider for Blizzard, including
              to benefit the Blizzard player community through our own services. In particular, we compile the
              information we obtain into our own databases, and make the information available to the Blizzard player
              community, including through the Raider.IO Addon, Raider.IO Desktop Client and the Raider.IO Developer
              API. If you link your Blizzard account to your Raider.IO account, we may collect and process additional
              information, which may be used in accordance with this privacy policy. Blizzard, and not us, determines
              what information is shared with us, and if you wish to not have your information shared by Blizzard or
              used by us in this way, please contact Blizzard directly and restrict data sharing in accordance with
              Blizzard’s instructions. We are not responsible for Blizzard’s data practices, and you should carefully
              review Blizzard’s policies and account settings.
            </p>
            <h3>Information collected from other sources</h3>
            <p>
              <i>In Short: We also collect information from business partners, public databases, and other sources.</i>
            </p>
            <p>
              We obtain information about you from other sources. We process the information in accordance with
              practices described in this privacy policy, subject to any restrictions imposed on us by the source of the
              information. The categories of sources from which we collect information, and have collected from in the
              past 12 months, include:{' '}
            </p>
            <ul className={styles.ul}>
              <li>
                Business partners, including to facilitate purchases and for event planning, joint marketing, or
                advertising purposes.
              </li>

              <li>Developers who use the Raider.IO Developer API.</li>

              <li>Social networks with which you interact. </li>

              <li>Publicly-available sources, including data in the public domain. </li>
            </ul>

            <h2>2. HOW DO WE USE YOUR INFORMATION?</h2>
            <p>
              <i>
                In Short: We process your information to provide you with services, offer databases, protect rights,
                improve and advertise our services, and for other purposes described below.
              </i>
            </p>
            <p>
              We collect and use information for business and commercial purposes in accordance with the practices
              described in this privacy policy. Our business purposes for collecting and using information, and for
              which we have collected and used information in the past 12 months, include:
            </p>
            <ul className={styles.ul}>
              <li>To operate and manage our Sites.</li>

              <li>To facilitate account creation and access.</li>

              <li>
                To develop and provide our databases for the benefit of the Blizzard player community, including through
                the Raider.IO Addon, Raider.IO Desktop Client and the Raider.IO Developer API.
              </li>

              <li>
                To request feedback, respond to your comments, questions and requests, and provide you with customer
                service.
              </li>

              <li>
                To send you technical notices, updates, security alerts, and support and administrative information.
              </li>

              <li>To enforce our terms, conditions and policies.</li>

              <li>To protect our Sites (for example, for fraud monitoring and prevention).</li>

              <li>
                To respond to legal requests and prevent harm. For example, if we receive a subpoena or other legal
                request, we may need to inspect the data we hold to determine how to respond.
              </li>

              <li>To enable user-to-user communications with your consent.</li>

              <li>
                To conduct data analysis, identify usage trends, determine the effectiveness of our promotional
                campaigns, and evaluate and improve our Sites, products, services, marketing, and your experience.
              </li>

              <li>
                To develop and send you marketing, advertising, and communications about our and other parties’
                products, services and features.
              </li>

              <li>
                To administer prize draws and competitions when you voluntarily elect to participate in competitions.
              </li>

              <li>
                To fulfil any other purpose at your direction, such as when you direct us to connect your Blizzard
                account to your Raider.IO account.
              </li>

              <li>With notice to you and your consent.</li>
            </ul>

            <p>
              Notwithstanding the above, we may use information that does not identify you (including information that
              has been aggregated or de-identified) for any purpose except as prohibited by applicable law. For
              information on your rights and choices regarding how we use information about you, please see the “What
              Are Your Privacy Rights?” section below.
            </p>
            <h2>3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</h2>
            <p>
              <i>
                In Short: We collect and share information with your consent or at your direction, to comply with laws,
                to protect your rights, to provide you with services, for analytics and marketing, or to fulfill
                business obligations.
              </i>
            </p>
            <p>
              We share your information in accordance with the practices described in this privacy policy. The
              categories of parties with whom we share information, and have shared information in the past 12 months,
              include the following:
            </p>
            <ul className={styles.ul}>
              <li>
                Service Providers. We share your information with service providers who process information on our
                behalf and require access to such information to do that work. Examples include payment processing, data
                analysis, email delivery, hosting services, customer service, and marketing efforts. We contractually
                prohibit our service providers from retaining, using, or disclosing information about you for any
                purpose other than performing the service for us, although we may permit them to use information that
                does not identify you (including information that has been aggregated or de-identified) for any purpose
                except as prohibited by applicable law.
              </li>

              <li>
                Vendors and Ad Partners. We share information with vendors and ad partners for analytics and advertising
                related purposes. These parties may act as our service providers, or in certain contexts, independently
                decide how to process your information. For more information on advertising and analytics, see the “What
                is Our Stance on Analytics and Advertising?” section below.
              </li>

              <li>
                Affiliates. We may share information with our affiliates and related entities, including where they act
                as our service providers or for their own internal purposes.
              </li>

              <li>
                Blizzard. We share information with Blizzard in our role as acting as a service provider for the
                company. We are not responsible for Blizzard’s data practices, and you should carefully review
                Blizzard’s policies and account settings.
              </li>

              <li>
                Blizzard Player Community. We share portions of our databases comprised of characters, gameplay,
                statistics, achievements, and other information with the Blizzard player community, including through
                the Raider.IO Addon and Raider.IO Desktop Client. If you do not want us to share your information this
                way, please contact Blizzard directly and restrict data sharing in accordance with Blizzard’s
                instructions.
              </li>

              <li>
                Developers. We share portions of our databases comprised of characters, gameplay, statistics,
                achievements, and other information with developers through the Raider.IO Developer API. If you do not
                want us to share your information this way, please contact Blizzard directly and restrict data sharing
                in accordance with instructions.
              </li>

              <li>
                Business Partners. We share information with our business partners including to facilitate purchases and
                for event planning, joint marketing, and advertising purposes.
              </li>

              <li>
                Compliance with Laws. We share information where we are legally required to do so in order to comply
                with applicable law, governmental requests, a judicial proceeding, court order, or legal process, such
                as in response to a court order or a subpoena (including in response to public authorities to meet
                national security or law enforcement requirements).
              </li>

              <li>
                Vital Interests and Legal Rights. We may disclose your information where we believe it is necessary to
                investigate, prevent, or take action regarding potential violations of our policies, suspected fraud,
                situations involving potential threats to the safety of any person and illegal activities, or as
                evidence in litigation in which we are involved.
              </li>

              <li>
                Business Transfers. We share information in connection with, or during negotiations of, any merger,
                purchase, or sale of any or all of our assets, financing, or acquisition of all or a portion of our
                business to another company.
              </li>

              <li>
                Promotions. Our promotions may be jointly sponsored or offered by other parties. When you voluntarily
                enter a promotion, we share information as set out in the official rules that govern the promotion as
                well as for administrative purposes and as required by law (e.g., on a winners list). By entering a
                promotion, you agree to the official rules that govern that promotion, and may, except where prohibited
                by applicable law, allow the sponsor and/or other entities to use your name, voice and/or likeness in
                advertising or marketing materials.
              </li>

              <li>
                Public Information and Chat. We share information you make public through the Sites, such as information
                in your profile. Please think carefully before making information public as you are solely responsible
                for any information you make public. Once you have posted information, you may not be able to edit or
                delete such information, subject to additional rights set out in the “What Are Your Privacy Rights?”
                section below. We also share information with other users when you engage with them through our chat
                functionality.
              </li>

              <li>Facilitating Requests. We share information at your request or direction.</li>

              <li>With your Consent. We may disclose your information for any other purpose with your consent.</li>
            </ul>

            <p>
              Notwithstanding the above, we may share information that does not identify you (including information that
              has been aggregated or de-identified) except as prohibited by applicable law. For information on your
              rights and choices regarding how we share information about you, please see the “What Are Your Privacy
              Rights?” section below.
            </p>

            <h3>Mediavine Programmatic Advertising (Ver 1.1)</h3>
            <p>
              The Website works with Mediavine to manage third-party interest-based advertising appearing on the
              Website. Mediavine serves content and advertisements when you visit the Website, which may use first and
              third-party cookies. A cookie is a small text file which is sent to your computer or mobile device
              (referred to in this policy as a “device”) by the web server so that a website can remember some
              information about your browsing activity on the Website.
            </p>
            <p>
              First party cookies are created by the website that you are visiting. A third-party cookie is frequently
              used in behavioral advertising and analytics and is created by a domain other than the website you are
              visiting. Third-party cookies, tags, pixels, beacons and other similar technologies (collectively, “Tags”)
              may be placed on the Website to monitor interaction with advertising content and to target and optimize
              advertising. Each internet browser has functionality so that you can block both first and third-party
              cookies and clear your browser’s cache. The "help" feature of the menu bar on most browsers will tell you
              how to stop accepting new cookies, how to receive notification of new cookies, how to disable existing
              cookies and how to clear your browser’s cache. For more information about cookies and how to disable them,
              you can consult the information at{' '}
              <a
                href="https://www.allaboutcookies.org/manage-cookies/"
                target="_blank"
                rel="noreferrer noopener nofollow"
              >
                All About Cookies
              </a>
              .
            </p>
            <p>
              Without cookies you may not be able to take full advantage of the Website content and features. Please
              note that rejecting cookies does not mean that you will no longer see ads when you visit our Site. In the
              event you opt-out, you will still see non-personalized advertisements on the Website.
            </p>
            <p>The Website collects the following data using a cookie when serving personalized ads:</p>
            <ul>
              <li>IP Address</li>
              <li>Operating System type</li>
              <li>Operating System version</li>
              <li>Device Type</li>
              <li>Language of the website</li>
              <li>Web browser type</li>
              <li>Email (in hashed form)</li>
            </ul>
            <p>
              Mediavine Partners (companies listed below with whom Mediavine shares data) may also use this data to link
              to other end user information the partner has independently collected to deliver targeted advertisements.
              Mediavine Partners may also separately collect data about end users from other sources, such as
              advertising IDs or pixels, and link that data to data collected from Mediavine publishers in order to
              provide interest-based advertising across your online experience, including devices, browsers and apps.
              This data includes usage data, cookie information, device information, information about interactions
              between users and advertisements and websites, geolocation data, traffic data, and information about a
              visitor’s referral source to a particular website. Mediavine Partners may also create unique IDs to create
              audience segments, which are used to provide targeted advertising.
            </p>
            <p>
              If you would like more information about this practice and to know your choices to opt-in or opt-out of
              this data collection, please visit{' '}
              <TrackedLink href="https://thenai.org/opt-out/" target="_blank" rel="noreferrer noopener nofollow">
                National Advertising Initiative opt out page
              </TrackedLink>
              . You may also visit{' '}
              <TrackedLink href="http://optout.aboutads.info/#/" target="_blank" rel="noreferrer noopener nofollow">
                Digital Advertising Alliance website
              </TrackedLink>{' '}
              and{' '}
              <TrackedLink
                href="http://optout.networkadvertising.org/#"
                target="_blank"
                rel="noreferrer noopener nofollow"
              >
                Network Advertising Initiative website
              </TrackedLink>{' '}
              to learn more information about interest-based advertising. You may download the AppChoices app at{' '}
              <TrackedLink
                href="https://youradchoices.com/appchoices"
                target="_blank"
                rel="noreferrer noopener nofollow"
              >
                Digital Advertising Alliance’s AppChoices app
              </TrackedLink>{' '}
              to opt out in connection with mobile apps, or use the platform controls on your mobile device to opt out.
            </p>
            <p>
              For specific information about Mediavine Partners, the data each collects and their data collection and
              privacy policies, please visit{' '}
              <TrackedLink
                href="https://www.mediavine.com/ad-partners/"
                target="_blank"
                rel="noreferrer noopener nofollow"
              >
                Mediavine Partners
              </TrackedLink>
              .
            </p>

            {/* <p>
              All or partial advertising on this Website or App is managed by Playwire LLC. If Playwire publisher
              advertising services are used, Playwire LLC may collect and use certain aggregated and anonymized data for
              advertising purposes. To learn more about the types of data collected, how data is used and your choices
              as a user, please visit{' '}
              <TrackedLink href="https://www.playwire.com/privacy-policy" target="_blank">
                https://www.playwire.com/privacy-policy
              </TrackedLink>
              .
            </p> */}

            <p>
              Raider.IO's use and transfer to any other app of information received from Google APIs will adhere to{' '}
              <TrackedLink
                href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
                target="_blank"
              >
                Google API Services User Data Policy
              </TrackedLink>
              , including the Limited Use requirements.
            </p>

            <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
            <p>
              <i>In Short: We use cookies and other tracking technologies to collect and store your information.</i>
            </p>
            <p>The types of tracking technologies we use to automatically collect information include:</p>
            <ul className={styles.ul}>
              <li>
                Log Files, which are files that record events that occur in connection with your use of the Service.
              </li>
              <li>
                Cookies, which are small data files stored on your device that act as a unique tag to identify your
                browser. We use two types of cookies: session cookies and persistent cookies. Session cookies make it
                easier for you to navigate our website and expire when you close your browser. Persistent cookies help
                with personalizing your experience, remembering your preferences, and supporting security features.
                Additionally, persistent cookies allow us to bring you advertising both on and off the Sites. Persistent
                cookies may remain on your device for extended periods of time, and generally may be controlled through
                your browser settings.
              </li>
              <li>
                Pixels (also known as web beacons), which are code embedded in a website, video, email, or advertisement
                that sends information about your use to a server. There are various types of pixels, including image
                pixels (which are small graphic images) and JavaScript pixels (which contains JavaScript code). When you
                access a website, video, email, or advertisement that contains a pixel, the pixel may permit us or a
                separate entity to drop or read cookies on your browser. Pixels are used in combination with cookies to
                track activity by a particular browser on a particular device. We may incorporate pixels from separate
                entities that allow us track our conversions, bring you advertising both on and off the Sites, and
                provide you with additional functionality.
              </li>
            </ul>
            <p>
              For further information on how we use tracking technologies for analytics and advertising, and your rights
              and choices regarding them, see the “What is Our Stance on Analytics and Advertising?” and “What Are Your
              Privacy Rights?” sections below. Specific information about how we use such technologies and how you may
              be able to refuse certain cookies is also set out in our Cookies Policy:{' '}
              <Link to="/cookies-policy">https://raider.io/cookies-policy</Link>.
            </p>
            <h2>5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</h2>
            <p>
              The Sites may contain advertisements from and links to third party sites that are not affiliated with us
              and which may link to other websites, online services or mobile applications. We cannot guarantee the
              safety and privacy of information you provide to any other parties. Any information collected by other
              parties is not covered by this privacy policy. We are not responsible for the content or privacy and
              security practices and policies of any other parties, including other websites, services or applications
              that may be linked to or from the Sites. You should review the policies of such other parties and contact
              them directly to respond to your questions.
            </p>
            <h2>6. WHAT IS OUR STANCE ON ANALYTICS AND ADVERTISING?</h2>
            <p>
              <i>
                In Short: We use analytics and advertising on our Sites, and we and other parties may collect
                information through tracking technologies.
              </i>
            </p>
            <p>
              We use analytics services, such as Google Analytics, to help us understand how users access and use the
              Sites. In addition, we work with agencies, advertisers, ad networks, and other ad partners to place ads on
              our Sites and on other websites and services. As part of this process, we may incorporate tracking
              technologies into our own Sites (including emails and ads) as well as into our ads displayed on other
              websites and services. Some of these tracking technologies may track your activities across time and
              services for purposes of associating the different devices you use, and delivering relevant ads and/or
              other content to you (“Interest-based Advertising”).
            </p>
            <p>
              As indicated above, vendors and ad partners may act as our service providers, or in certain contexts,
              independently decide how to process your information. We encourage you to familiarize yourself with and
              consult their privacy policies and terms of use.
            </p>
            <p>
              For further information on the types of tracking technologies we use on the Service and your rights and
              choices regarding analytics and Interest-based Advertising please see the “Do We Use Cookies And Other
              Tracking Technologies?” and “What Are Your Privacy Rights?” sections.
            </p>
            <h2>7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
            <p>
              <i>
                In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this
                privacy policy unless otherwise required by law or to protect rights.
              </i>
            </p>
            <p>
              We will keep your information for as long as it is necessary for the purposes set out in this privacy
              policy, unless a longer retention period is required or permitted by law (such as tax, accounting or other
              legal requirements).
            </p>
            <h2>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
            <p>
              <i>
                In Short: We aim to protect your information through a system of organizational and technical security
                measures.
              </i>
            </p>
            <p>
              We have implemented appropriate technical and organizational security measures designed to protect the
              security of information about you that we process. However, please also remember that we cannot guarantee
              that the Internet itself is 100% secure. Although we will do our best to protect your information,
              transmission of information to and from our Sites is at your own risk. You should only access the services
              within a secure environment.
            </p>
            <h2>9. DO WE COLLECT INFORMATION FROM CHILDREN?</h2>
            <p>
              <i>In Short: We do not knowingly collect information from children under 13 years of age.</i>
            </p>
            <p>
              The Sites are intended for general audiences. We do not knowingly collect personal information (as defined
              by the U.S. Children’s Privacy Protection Act, or “COPPA”) from children under 13 years of age. If you are
              a parent or guardian and believe we have collected personal information in violation of COPPA, contact us
              at privacy@raider.io. We will remove the personal information in accordance with COPPA. We do not
              knowingly “sell,” as that term is defined under the CCPA, the personal information of minors under 16
              years old who are California residents.
            </p>
            <p>
              By using the Sites, you represent that you are at least 18. If we learn that information from users less
              than 18 years of age has been collected, we will deactivate the account and take reasonable measures to
              promptly delete such data from our records. If you become aware of any data we have collected from
              individuals under age 18, please contact us at privacy@raider.io.
            </p>
            <h2>10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
            <p>
              <i>
                In Short: You may review, change, or terminate your account at any time, or exercise choice about
                certain analytics or advertising activities on the Sites. For data received from Blizzard, you should
                reach out directly to Blizzard regarding your choices. In some regions, such as the European Economic
                Area, Nevada, and California, you have additional rights as set out at the end of this privacy policy.
              </i>
            </p>
            <h3>Data Received from Blizzard</h3>
            <p>
              We develop and provide our databases for the benefit the Blizzard player community, including through the
              Raider.IO Addon, Raider.IO Desktop Client and the Raider.IO Developer API. Blizzard, and not us,
              determines what information is shared with us by them to create these databases, and accordingly what
              information we share with the Blizzard player community and Raider.IO developers. If you do not want your
              information shared with us in this way, or if you want us to not share the information with the Blizzard
              player community or Raider.IO developers, please contact Blizzard directly and restrict data sharing in
              accordance with Blizzard’s instructions.
            </p>
            <h3>Account information</h3>
            <p>
              If you have an account with us and would like to review or change the information you have submitted to us
              through your account or terminate your account, you can:
            </p>
            <ul className={styles.ul}>
              <li>Log into your account and update your user settings; or </li>

              <li>Contact us at privacy@raider.io.</li>
            </ul>

            <p>
              If you have linked your Raider.IO account with your Blizzard account, you can also unlink the accounts at
              any time through your Raider.IO account settings. Blizzard may also offer the ability to unlink the
              account, and you should review Blizzard’s policies and account settings.
            </p>
            <p>
              Upon your request to terminate your Raider.IO account, we will deactivate or delete your account, provided
              we may allow for a cooldown period in the event you change your mind. Even after termination, some
              information may be retained in our files to prevent fraud, troubleshoot problems, assist with any
              investigations, enforce our Terms of Use and/or comply with legal requirements.
            </p>
            <p>
              We may offer other options relating to character and profile visibility. Please visit your account
              settings for more information.
            </p>
            <h3>Cookies and similar technologies</h3>
            <p>
              Most Web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your
              browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this
              could affect certain features or services of our Sites. For further information, please see our Cookies
              Policy: <Link to="/cookies-policy">https://raider.io/cookies-policy</Link>.
            </p>
            <h3>Do Not Track</h3>
            <p>
              Your browser settings may allow you to automatically transmit a “Do Not Track” signal to online services
              you visit. Note, however, there is no industry consensus as to what site and app operators should do with
              regard to these signals. Accordingly, unless and until the law is interpreted to require us to do so, we
              do not monitor or take action with respect to “Do Not Track” signals. For more information on “Do Not
              Track,” visit https://www.allaboutdnt.com.
            </p>
            <h3>Analytics and interest-based advertising</h3>
            <p>
              Google provides tools to allow you to opt out of the use of certain information collected by Google
              Analytics at https://tools.google.com/dlpage/gaoptout and by Google Analytics for Display Advertising or
              the Google Display Network at https://www.google.com/settings/ads/onweb/.
            </p>
            <p>
              The companies we work with to provide you with targeted ads are required by us to give you the choice to
              opt out of receiving targeted ads. Most of these companies are participants of the Digital Advertising
              Alliance (“DAA”) and/or the Network Advertising Initiative (“NAI”). To learn more about the targeted ads
              provided by these companies, and how to opt out of receiving certain targeted ads from them, please visit:
              (i) for targeted ads from DAA participants, https://www.aboutads.info/choices; and (ii) for targeted ads
              from NAI participants, https://www.networkadvertising.org/choices/. Opting out only means that the
              selected participants should no longer deliver certain targeted ads to you, but does not mean you will no
              longer receive any targeted content and/or ads (e.g., in connection with the participants’ other customers
              or from other technology services).
            </p>
            <p>
              Please note that if you opt out using any of these methods, the opt out will only apply to the specific
              browser or device from which you opt out. We are not responsible for the effectiveness of, or compliance
              with, any opt out options or programs, or the accuracy of any other entities’ statements regarding their
              opt out options or programs.
            </p>
            <h3>Opting out of email marketing</h3>
            <p>
              You can unsubscribe from our marketing email list at any time by clicking on the unsubscribe link in the
              emails that we send or by contacting us as set out in the “How Can You Contact Us About This Policy?”
              section below. You will then be removed from the marketing email list – however, we may still send you
              service-related emails that are necessary for the administration and use of your account.
            </p>
            <h2>11. DO WE MAKE UPDATES TO THIS POLICY?</h2>
            <p>
              <i>
                In Short: Yes, we will update this policy as necessary to comply with relevant laws or reflect changes
                to our practices.
              </i>
            </p>
            <p>
              We may update this privacy policy from time to time. The updated version will be indicated by an updated
              “Revised” date and the updated version will be effective as soon as it is accessible. If we make material
              changes to this privacy policy, we may notify you either by prominently posting a notice of such changes
              or by directly sending you a notification. We encourage you to review this privacy policy frequently to be
              informed of how we are protecting your information.
            </p>
            <h2>12. HOW CAN YOU CONTACT US ABOUT THIS POLICY?</h2>
            <p>
              If you have questions or comments about this privacy policy or our data practices, you may contact us:
            </p>
            <p>By email: at privacy@raider.io </p>
            <p>By postal address: </p>
            <p>
              RaiderIO, Inc.
              <br />
              Attention: Privacy Compliance
              <br />
              1887 Whitney Mesa Dr #4968
              <br />
              Henderson, NV 89014
              <br />
              United States
            </p>

            <p>
              This privacy policy has been designed to be accessible to people with disabilities. If you experience any
              difficulties accessing the information here, please contact us at privacy@raider.io.
            </p>
            <h2>13. ADDITIONAL DISCLOSURES FOR NEVADA RESIDENTS</h2>

            <p>
              Nevada law (NRS 603A.340) requires each business to establish a designated request address where Nevada
              consumers may submit requests directing the business not to sell certain kinds of personal information
              that the business has collected or will collect about the consumer. A sale under Nevada law is the
              exchange of personal information for monetary consideration by the business to a third party for the third
              party to license or sell the personal information to other third parties. If you are a Nevada consumer and
              wish to submit a request relating to our compliance with Nevada law, please contact us as at
              privacy@raider.io.
            </p>

            <h2>14. ADDITIONAL DISCLOSURES FOR CALIFORNIA RESIDENTS</h2>

            <p>
              These additional disclosures apply only to California residents. The California Consumer Privacy Act of
              2018 (“CCPA”) provides additional rights to know, delete and opt-out, and requires businesses collecting
              or disclosing personal information to provide notices and means to exercise rights.
            </p>
            <h3>Notice of collection</h3>
            <p>
              In the past 12 months, we have collected the following categories of personal information enumerated in
              the CCPA:{' '}
            </p>
            <ul className={styles.ul}>
              <li>Identifiers, including email address and online identifiers (such as IP address).</li>
              <li>
                Commercial or transactions information, including records of products or services purchased, obtained,
                or considered.
              </li>
              <li>
                Internet activity, including browsing history, search history, and interactions with a website, email,
                application, or advertisement.
              </li>
              <li>Geolocation data inferred from an IP address or other device identifier.</li>
              <li>Inferences drawn from the above information about your predicted characteristics and preferences.</li>
            </ul>

            <p>
              For further details on information we collect, including the sources from which we receive information,
              review the “What Information Do We Collect?” section above. We collect and use these categories of
              personal information for the business purposes described in the “How Do We Use Your Information?” section
              above, including how to manage our Sites.
            </p>
            <p>
              We do not generally sell information as the term “sell” is traditionally understood. To the extent “sale”
              under the CCPA is interpreted to include the activities set out in this privacy policy, such as those
              disclosed in the “What is Our Stance on Analytics and Advertising?” section above, we will comply with
              applicable law as to such activity. We disclose the following categories of personal information for
              commercial purposes: identifiers, characteristics, commercial or transactions information, internet
              activity, geolocation data, and inferences drawn. Please review the “Will Your Information Be Shared With
              Anyone?” section above for further details about the categories of parties with whom we share information.
            </p>
            <h3>Right to know and delete</h3>

            <p>
              You have the right to know certain details about our data practices in the past 12 months. In particular,
              you may request the following from us:
            </p>
            <ul className={styles.ul}>
              <li>The categories of personal information we have collected about you; </li>
              <li>The categories of sources from which the personal information was collected; </li>
              <li>The categories of personal information about you we disclosed for a business purpose or sold; </li>
              <li>
                The categories of third parties to whom the personal information was disclosed for a business purpose or
                sold;{' '}
              </li>
              <li>The business or commercial purpose for collecting or selling the personal information; and </li>
              <li>The specific pieces of personal information we have collected about you.</li>
            </ul>

            <p>In addition, you have the right to delete the personal information we have collected from you. </p>
            <p>
              To exercise any of these rights, please submit a request through our{' '}
              <TrackedLink
                href="https://docs.google.com/forms/d/e/1FAIpQLSfkI9iiiWF3_7OPzKJZvuwahrHDVHu0CTtawycHSCqq5WHX_w/viewform?usp=sf_link"
                target="_blank"
              >
                Online Form
              </TrackedLink>{' '}
              or email us at privacy@raider.io. In the request, please specify which right you are seeking to exercise
              and the scope of the request. We will confirm receipt of your request within 10 days. We may require
              specific information from you to help us verify your identity and process your request. If we are unable
              to verify your identity, we may deny your requests to know or delete.
            </p>
            <p>
              We acknowledge that you may have rights under the CCPA in connection with the personal information we
              process as a service provider for Blizzard. If you wish to exercise any rights you have with such personal
              information, please direct your request to Blizzard. We will support Blizzard to the extent required by
              applicable law in responding to your request.
            </p>

            <h3 id="do_not_sell">Right to opt-out</h3>

            <p>
              To the extent Raider.IO sells your personal information as the term “sell” is defined under the CCPA, you
              have the right to opt-out of the sale of your personal information by us to third parties at any time. You
              may submit a request to opt-out by clicking{' '}
              <Link to={namedRoutes.PrivacyPolicy} onClick={this.showConsentUi}>
                Do Not Sell My Personal Information
              </Link>{' '}
              or emailing us at privacy@raider.io.
            </p>
            <h3>Authorized agent</h3>

            <p>
              You can designate an authorized agent to submit requests on your behalf. However, we will require written
              proof of the agent’s permission to do so and verify your identity directly.
            </p>
            <h3>Right to non-discrimination</h3>

            <p>You have the right not to receive discriminatory treatment by us for the exercise of any your rights.</p>
            <h3>Shine the light</h3>
            <p>
              Customers who are residents of California may request (i) a list of the categories of personal information
              disclosed by us to third parties during the immediately preceding calendar year for those third parties’
              own direct marketing purposes; and (ii) a list of the categories of third parties to whom we disclosed
              such information. To exercise a request, please write us at the email or postal address set out in “How
              Can You Contact Us About This Policy?” above and specify that you are making a “California Shine the Light
              Request.” We may require additional information from you to allow us to verify your identity and are only
              required to respond to requests once during any calendar year.
            </p>

            <h2>15. ADDITIONAL DISCLOSURES FOR DATA SUBJECTS IN EUROPE</h2>
            <h3>Roles</h3>
            <p>
              Data protection laws in Europe distinguish between organizations that process personal data for their own
              purposes (known as “controllers”) and organizations that process personal data on behalf of other
              organizations (known as “processors”). Raider.IO acts as a controller with respect to personal data
              collected as you interact with our websites, emails, and advertisements. However, in some instances,
              Raider.IO acts as a processor where we process personal data on behalf of Blizzard, which is the
              controller. Any questions that you have relating to the processing of personal data by us as a processor
              should be directed to Blizzard.
            </p>

            <h3>Lawful basis for processing</h3>
            <p>
              Data protection laws in Europe require a “lawful basis” for processing personal data. Our lawful bases
              include where: (a) you have given consent to the processing for one or more specific purposes, either to
              us, to our service providers, or to Blizzard; (b) processing is necessary for the performance of a
              contract with you; (c) processing is necessary for compliance with a legal obligation; or (d) processing
              is necessary for the purposes of the legitimate interests pursued by us or a third party, and your
              interests and fundamental rights and freedoms do not override those interests. Where applicable, we will
              transfer your personal data to third countries subject to appropriate or suitable safeguards, such as
              standard contractual clauses.
            </p>

            <h3>Your data subject rights</h3>
            <p>
              You have the right to access, rectify, or erase any personal data we have collected about you. You also
              have the right to data portability and the right to restrict or object to our processing of personal data
              we have collected about you. In addition, you have the right to ask us not to process your personal data
              (or provide it to third parties to process) for marketing purposes or purposes materially different than
              for which it was originally collected or subsequently authorized by you. You may withdraw your consent at
              any time for any data processing we do based on consent you have provided to us.
            </p>

            <p>
              To exercise any of these rights, please contact us as set out in the “How Can You Contact Us About This
              Policy?” section above and specify which right you are seeking to exercise. We will respond to your
              request within 30 days. We may require specific information from you to help us confirm your identity and
              process your request. If personal data about you has been processed by us as a processor for Blizzard and
              you wish to exercise any rights you have with such personal data, please direct your request to Blizzard.
              We will support Blizzard to the extent required by applicable law in responding to your request.
            </p>

            <p>
              Please note that we retain personal data as necessary to fulfill the purposes for which it was collected,
              and may continue to retain and use personal data even after a data subject request for purposes of our
              legitimate interests, including as necessary to comply with our legal obligations, resolve disputes,
              prevent fraud, and enforce our agreements. When we have no ongoing legitimate interest to process your
              personal data, we will delete, de-identify, or anonymize it, or, if this is not possible (for example,
              because your personal data has been stored in backup archives), then we will implement measures to
              securely store your personal data and isolate it from any further processing until deletion is possible.
            </p>
            <p>
              If you have any issues with our compliance, you may contact us as set out in the “How Can You Contract Us
              About This Policy” section above. You also have the right to lodge a complaint with the data protection
              regulator in your jurisdiction.
            </p>
          </div>
        </div>
      </>
    );
  }

  private showConsentUi(ev: any): void {
    ev.preventDefault();
    const { __tcfapi } = window as any;
    if (typeof __tcfapi === 'function') {
      __tcfapi('displayConsentUi', 2, () => {
        /* nop */
      });
    }
  }
}
